import { BoxTitles } from "@boxes/BoxTitles";
import { Button, Checkbox, Drawer, Hairline, Label, Steps } from "@components";
import { useEffect, useState } from "react";

import styles from "@styles/forms-flow/orders.module.scss";
import { BrandsAndUnitsForm } from "./orders/BrandsAndUnitsForm";
import { LastConversionForm } from "./orders/LastConversionForm";
import { OrderCountForm } from "./orders/OrderCountForm";
import { OriginForm } from "./orders/OriginForm";
import { TimeForm } from "./orders/TimeForm";

const forms = {
  ORDERS_QUANTITY: "ORDERS_QUANTITY",
  BRANDS_AND_UNITS: "BRANDS_AND_UNITS",
  ORDERS_ORIGIN: "ORDERS_ORIGIN",
  TIME: "TIME",
  LAST_CONVERSION: "LAST_CONVERSION",
};

export const OrderConditionalForm = ({
  visible,
  onClose,
  node,
  onChange,
  onSave,
}) => {
  const [steps, setSteps] = useState(node?.data?.filters || []);
  const [createdSteps, setCreatedSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);

  const getFreshNode = (data) => {
    return {
      ...node,
      data: {
        ...node.data,
        ...data,
      },
    };
  };

  const handleSelectForm = (newForm) => {
    let newSteps = [...steps];

    const index = newSteps.indexOf(newForm);

    if (index !== -1) {
      newSteps = steps.filter((step) => step !== newForm);
    } else newSteps.push(newForm);

    setSteps(newSteps);
  };

  useEffect(() => {
    if (visible) {
      setSteps(node?.data?.filters || []);
    }
  }, [visible]);

  const getCurrentStep = () => {
    const ORDERS_QUANTITY = steps.includes(forms.ORDERS_QUANTITY);
    const BRANDS_AND_UNITS = steps.includes(forms.BRANDS_AND_UNITS);
    const ORDERS_ORIGIN = steps.includes(forms.ORDERS_ORIGIN);
    const LAST_CONVERSION = steps.includes(forms.LAST_CONVERSION);

    if (ORDERS_QUANTITY && currentStep == forms.ORDERS_QUANTITY) {
      return 1;
    }

    if (BRANDS_AND_UNITS && currentStep == forms.BRANDS_AND_UNITS) {
      return ORDERS_QUANTITY + BRANDS_AND_UNITS;
    }

    if (ORDERS_ORIGIN && currentStep == forms.ORDERS_ORIGIN) {
      return ORDERS_QUANTITY + BRANDS_AND_UNITS + ORDERS_ORIGIN;
    }

    if (LAST_CONVERSION && currentStep == forms.LAST_CONVERSION) {
      return (
        LAST_CONVERSION + ORDERS_QUANTITY + BRANDS_AND_UNITS + ORDERS_ORIGIN
      );
    }
  };

  const subtitles = {
    [forms.ORDERS_QUANTITY]: `${getCurrentStep()} de ${
      steps.length
    } - Quantidade de pedidos`,
    [forms.BRANDS_AND_UNITS]: `${getCurrentStep()} de ${
      steps.length
    } - Marcas e Unidades`,
    [forms.ORDERS_ORIGIN]: `${getCurrentStep()} de ${
      steps.length
    } - Origem dos pedidos`,
    [forms.LAST_CONVERSION]: `${getCurrentStep()} de ${
      steps.length
    } - Dias sem pedir`,
    [forms.TIME]: "Tempo",
  };

  const nextStep = () => {
    const ORDERS_QUANTITY = steps.includes(forms.ORDERS_QUANTITY);
    const BRANDS_AND_UNITS = steps.includes(forms.BRANDS_AND_UNITS);
    const ORDERS_ORIGIN = steps.includes(forms.ORDERS_ORIGIN);
    const LAST_CONVERSION = steps.includes(forms.LAST_CONVERSION);

    const steps_ordered = [
      ORDERS_QUANTITY ? "ORDERS_QUANTITY" : null,
      BRANDS_AND_UNITS ? "BRANDS_AND_UNITS" : null,
      ORDERS_ORIGIN ? "ORDERS_ORIGIN" : null,
      LAST_CONVERSION ? "LAST_CONVERSION" : null,
    ].filter((s) => s);

    steps_ordered.push("TIME");

    const stepIndex = steps_ordered.indexOf(currentStep);
    setCurrentStep(steps_ordered[stepIndex + 1]);
  };

  const prevStep = () => {
    const ORDERS_QUANTITY = steps.includes(forms.ORDERS_QUANTITY);
    const BRANDS_AND_UNITS = steps.includes(forms.BRANDS_AND_UNITS);
    const ORDERS_ORIGIN = steps.includes(forms.ORDERS_ORIGIN);
    const LAST_CONVERSION = steps.includes(forms.LAST_CONVERSION);

    const steps_ordered = [
      ORDERS_QUANTITY ? "ORDERS_QUANTITY" : null,
      BRANDS_AND_UNITS ? "BRANDS_AND_UNITS" : null,
      ORDERS_ORIGIN ? "ORDERS_ORIGIN" : null,
      LAST_CONVERSION ? "LAST_CONVERSION" : null,
    ].filter((s) => s);

    steps_ordered.push("TIME");

    const stepIndex = steps_ordered.indexOf(currentStep);
    setCurrentStep(steps_ordered[stepIndex - 1] || null);
  };

  const onCreateSteps = () => {
    let newSteps = [];

    const node_data = { ...(node.data || {}) };

    if (steps.includes(forms.ORDERS_QUANTITY)) {
      newSteps.push(forms.ORDERS_QUANTITY);
    } else {
      node_data.min = undefined;
      node_data.max = undefined;
      node_data.exact = undefined;
    }

    if (steps.includes(forms.BRANDS_AND_UNITS)) {
      newSteps.push(forms.BRANDS_AND_UNITS);
    } else {
      node_data.units = [];
    }

    if (steps.includes(forms.ORDERS_ORIGIN)) {
      newSteps.push(forms.ORDERS_ORIGIN);
    } else {
      node_data.origins = [];
    }

    if (steps.includes(forms.LAST_CONVERSION)) {
      newSteps.push(forms.LAST_CONVERSION);
    } else {
      node_data.last_conversion = null;
    }

    newSteps.push(forms.TIME);

    setCreatedSteps(newSteps);
    node_data.filters = newSteps;

    setCurrentStep(newSteps[0]);

    onChangeOrders(node_data);
  };

  const close = () => {
    setCurrentStep(null);
    setSteps([]);
    onClose();
  };

  const save = () => {
    setCurrentStep(null);
    setSteps([]);
    onSave(getFreshNode({}));
  };

  const onChangeOrders = (data) => {
    const n = getFreshNode(data);
    onChange && onChange(n);
    return n;
  };

  return (
    <Drawer
      visible={visible}
      subtitle={subtitles[forms[currentStep]]}
      onClose={close}
      showBackButton={currentStep}
      backButton={prevStep}
      title={BoxTitles.OrderFilter}
    >
      {!currentStep && (
        <>
          <Label>O que você gostaria de filtrar?</Label>
          <div
            className={styles.item}
            onClick={() => handleSelectForm(forms.ORDERS_QUANTITY)}
          >
            <Checkbox checked={steps.includes(forms.ORDERS_QUANTITY)}>
              Quantidade de pedidos
            </Checkbox>
          </div>
          <div
            className={styles.item}
            onClick={() => handleSelectForm(forms.BRANDS_AND_UNITS)}
          >
            <Checkbox checked={steps.includes(forms.BRANDS_AND_UNITS)}>
              Marcas e Unidades
            </Checkbox>
          </div>
          <div
            className={styles.item}
            onClick={() => handleSelectForm(forms.ORDERS_ORIGIN)}
          >
            <Checkbox checked={steps.includes(forms.ORDERS_ORIGIN)}>
              Origem dos pedidos
            </Checkbox>
          </div>
          <div
            className={styles.item}
            onClick={() => handleSelectForm(forms.LAST_CONVERSION)}
          >
            <Checkbox checked={steps.includes(forms.LAST_CONVERSION)}>
              Dias sem pedir
            </Checkbox>
          </div>

          <div className={styles.footer}>
            <Button
              className="w-100"
              disabled={steps.length === 0}
              onClick={onCreateSteps}
              design="primary"
            >
              Continuar
            </Button>
          </div>
        </>
      )}

      {currentStep && (
        <>
          <Steps
            steps={createdSteps.length}
            currentStep={
              currentStep === forms.TIME
                ? createdSteps.length
                : getCurrentStep()
            }
          />

          <Hairline className="mt-30 mb-30" />

          <div className={styles.footer}>
            {currentStep !== forms.TIME && (
              <Button className="w-100" onClick={nextStep} design="primary">
                Continuar
              </Button>
            )}

            {currentStep === forms.TIME && (
              <Button className="w-100" onClick={save} design="primary">
                Salvar
              </Button>
            )}
          </div>
        </>
      )}

      {currentStep === forms.ORDERS_QUANTITY && (
        <OrderCountForm node={node} onChange={(d) => onChangeOrders(d.data)} />
      )}

      {currentStep === forms.BRANDS_AND_UNITS && (
        <BrandsAndUnitsForm
          node={node}
          onChange={(d) => onChangeOrders({ units: d.data?.units || [] })}
        />
      )}

      {currentStep === forms.ORDERS_ORIGIN && (
        <OriginForm
          node={node}
          onChange={(d) => onChangeOrders({ origins: d.data?.origins || [] })}
        />
      )}

      {currentStep === forms.LAST_CONVERSION && (
        <LastConversionForm
          node={node}
          onChange={(d) =>
            onChangeOrders({ last_conversion: d.data?.last_conversion || {} })
          }
        />
      )}

      {currentStep === forms.TIME && (
        <TimeForm node={node} onChange={(d) => onChangeOrders(d.data)} />
      )}
    </Drawer>
  );
};
